"use client";

import { useEffect, useState } from "react";
import Button from "src/components/common/Button/Button";
import Link from "src/components/common/Link/Link";
import Typography from "src/components/common/Typography/Typography";
import { saveCookieConsent, useCookieConsent } from "src/lib/CookieConsent";
import { useExtracted } from "#i18n";
import styles from "./CookieConsentBanner.module.scss";

/**
 * Browsers driven by automation (WebDriver, Playwright, headless Chrome) or by an AI agent, such
 * as the Claude app's built-in browser. Nobody is there to answer, so no choice is asked for and
 * functional cookies stay off.
 */
const isAutomatedBrowser = () =>
  navigator.webdriver === true || /\bHeadlessChrome\/|\bClaude\//.test(navigator.userAgent);

/**
 * Site-wide cookie notice, shown until the visitor makes a choice for the current inventory
 * version. Nothing optional is pre-selected, and "Essential only" is exactly as prominent as
 * "Accept all". It renders on the server for visitors without a choice, so it doesn't pop in.
 */
const CookieConsentBanner = () => {
  const translateMessage = useExtracted();
  const { ready, consent } = useCookieConsent();
  // Only the browser can tell; the server must not vary cached HTML by user agent.
  const [automated, setAutomated] = useState(false);
  useEffect(() => setAutomated(isAutomatedBrowser()), []);

  if (!ready || consent || automated) {
    return null;
  }

  return (
    <section
      className={styles.banner}
      aria-label={translateMessage("Cookie choices")}
      data-cookie-consent-banner={true}
    >
      <div className={styles.content}>
        <Typography as="h2" variant="heading4">
          {translateMessage("Cookies on CalmLens")}
        </Typography>
        <Typography variant="bodySmall" tone="muted">
          {translateMessage(
            "We use essential cookies for sign-in and security. With your permission, functional cookies remember your preferences and Google Analytics measures website use and signups. Optional ad measurement cookies link Google ad clicks to signups. Personalised advertising stays off.",
          )}{" "}
          <Link href="/cookies" variant="hyperlink">
            {translateMessage("Manage cookies")}
          </Link>
        </Typography>
      </div>
      <div className={styles.actions}>
        <Button
          variant="outline"
          label={translateMessage("Essential only")}
          onClick={() => saveCookieConsent({ functional: false })}
        />
        <Button
          variant="outline"
          label={translateMessage("Accept all")}
          onClick={() => saveCookieConsent({ functional: true, analytics: true, marketing: true })}
        />
      </div>
    </section>
  );
};

export default CookieConsentBanner;
